import { MapPin, Phone, Mail } from 'lucide-react';
import WhatsAppChatButton from './WhatsAppChatButton';

interface FooterProps {
    // Retained for backwards compatibility with existing callers; the CTA now opens
    // a WhatsApp chat instead of the call scheduler.
    onBookMeeting?: () => void;
}

const Footer = (_props: FooterProps) => {
    return (
        <footer className="bg-black text-white">
            {/* Book a Meeting Section */}
            <div className="border-b border-gray-800">
                <div className="mx-auto px-20 py-12">
                    <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                        <div className="text-center md:text-left">
                            <h2 className="text-2xl md:text-3xl font-semibold">Ready to get started?</h2>
                            <p className="text-gray-400 text-sm mt-2">
                                Talk to our team and find the right check for your needs.
                            </p>
                        </div>
                        <WhatsAppChatButton className="bg-[#25D366] hover:bg-[#1EBE5B] text-white px-8 py-4 rounded-full font-medium transition-colors whitespace-nowrap inline-flex items-center gap-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#25D366] focus-visible:ring-offset-2 focus-visible:ring-offset-black" />
                    </div>
                </div>
            </div>

            {/* Logo + Company Information Section */}
            <div className="border-b border-gray-800">
                <div className="mx-auto px-20 py-10 grid grid-cols-1 md:grid-cols-2 gap-10">
                    {/* Logo */}
                    <div>
                        <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-[#FF6B35] rounded flex items-center justify-center">
                                <svg
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    className="w-5 h-5 text-white"
                                >
                                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                                </svg>
                            </div>
                            <span className="text-xl font-semibold">YourChecka</span>
                        </div>
                        <p className="text-gray-400 text-sm mt-4 max-w-sm leading-relaxed">
                            Real checks. Real answers. We help families and businesses make high-trust hires.
                        </p>
                    </div>

                    {/* Company Information */}
                    <div className="md:justify-self-end">
                        <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-300 mb-4">
                            Company Information
                        </h3>
                        <ul className="space-y-3 text-sm text-gray-400">
                            <li className="flex items-start gap-3">
                                <MapPin className="w-4 h-4 text-[#FF6B35] shrink-0 mt-0.5" />
                                <span>No. 5 Atiba Street, Bodija, Ibadan.</span>
                            </li>
                            <li className="flex items-start gap-3">
                                <MapPin className="w-4 h-4 text-[#FF6B35] shrink-0 mt-0.5" />
                                <span>42 Montgomery Road, Yaba, Lagos.</span>
                            </li>
                            <li className="flex items-start gap-3">
                                <MapPin className="w-4 h-4 text-[#FF6B35] shrink-0 mt-0.5" />
                                <span>1 Aba Close, Area 8, Garki, Abuja.</span>
                            </li>
                            <li className="flex items-start gap-3">
                                <Phone className="w-4 h-4 text-[#FF6B35] shrink-0 mt-0.5" />
                                {/* href must match the number on screen — these had
                                    drifted apart, so the link dialled a different line. */}
                                <a href="tel:+2348064277731" className="hover:text-white transition-colors">
                                    08064277731
                                </a>
                            </li>
                            <li className="flex items-start gap-3">
                                <Mail className="w-4 h-4 text-[#FF6B35] shrink-0 mt-0.5" />
                                <a href="mailto:hi@yourchecka.com" className="hover:text-white transition-colors">
                                    hi@yourchecka.com
                                </a>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>

            {/* Copyright + policy links. Both point at the help centre with
                ?product=yourchecka, which is the single source for the Yourchecka
                policy — the same URLs the consent flow and sign-up use, so the
                three surfaces can never drift apart. */}
            <div className="mx-auto px-6 py-6 sm:px-10 lg:px-20">
                <div className="flex flex-col items-center gap-3 sm:flex-row sm:justify-between">
                    <p className="text-gray-400 text-sm">© 2026 YourChecka. All Rights Reserved.</p>
                    <nav aria-label="Legal" className="flex items-center gap-5 text-sm">
                        <a
                            href="https://help.podus.org/privacy?product=yourchecka"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-gray-400 hover:text-white transition-colors"
                        >
                            Privacy Policy
                        </a>
                        <a
                            href="https://help.podus.org/terms?product=yourchecka"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-gray-400 hover:text-white transition-colors"
                        >
                            Terms of Use
                        </a>
                    </nav>
                </div>
            </div>
        </footer>
    );
};

export default Footer;
